import React, { useEffect, useState } from 'react';
import {
  Autocomplete, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, InputLabel, MenuItem,
  Select, TextField
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import Swal from 'sweetalert2';
import axios from 'axios';
import { myApi } from '../../../config/axiosConfig';

const initialForm = {
  date: null,
  asset_type: '',
  asset: '',
  driver: '',
  gallons: '',
  amount: ''
};

const CrearEditarFuelAmerica = ({ open, onClose, carga, onSaved }) => {
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [tractorCatalog, setTractorCatalog] = useState([]);
  const [cajaCatalog, setCajaCatalog] = useState([]);
  const [operadores, setOperadores] = useState([]);
  const [loadingCatalogs, setLoadingCatalogs] = useState(true);
  const isEditing = Boolean(carga?.id);

  const companies = ['EBT', 'TEDEL'];
  const statuses = ['A', 'I', 'S'];

  useEffect(() => {
    if (!open) return;
    setForm(carga ? {
      date: carga.date ? dayjs(carga.date) : null,
      asset_type: carga.asset_type || '',
      asset: carga.asset || '',
      driver: carga.driver || '',
      gallons: carga.gallons ?? '',
      amount: carga.amount ?? ''
    } : initialForm);
  }, [open, carga]);

  useEffect(() => {
    const cargarCatalogos = async () => {
      setLoadingCatalogs(true);

      try {
        const tractorRequests = [];
        const cajaRequests = [];

        companies.forEach((company) => {
          statuses.forEach((status) => {
            tractorRequests.push(
              axios.post(
                'https://apigeneral.ebtapps.com/general/api/mcleod/trucks/',
                { company, status, tractor: null },
                { timeout: 15000 }
              )
            );

            cajaRequests.push(
              axios.post(
                'https://apigeneral.ebtapps.com/general/api/mcleod/trailers',
                { company, status, trailer: null },
                { timeout: 15000 }
              )
            );
          });
        });

        const [tractorResults, cajaResults, operadoresResponse] = await Promise.all([
          Promise.allSettled(tractorRequests),
          Promise.allSettled(cajaRequests),
          myApi.get('/fuel_report/getOperadores')
        ]);

        const tractors = tractorResults
          .filter((result) => result.status === 'fulfilled')
          .flatMap((result) => result.value.data || []);

        const cajas = cajaResults
          .filter((result) => result.status === 'fulfilled')
          .flatMap((result) => result.value.data || []);

        const uniqueTractors = Array.from(
          new Map(
            tractors
              .filter((item) => item?.tractor != null)
              .map((item) => [String(item.tractor).trim(), item])
          ).values()
        );

        const uniqueCajas = Array.from(
          new Map(
            cajas
              .filter((item) => item?.trailer != null)
              .map((item) => [String(item.trailer).trim(), item])
          ).values()
        );

        setTractorCatalog(uniqueTractors.map((item) => ({
          value: String(item.tractor).trim(),
          label: String(item.tractor).trim()
        })));

        setCajaCatalog(uniqueCajas.map((item) => ({
          value: String(item.trailer).trim(),
          label: String(item.trailer).trim()
        })));

        setOperadores((operadoresResponse.data.data || []).map((operador) => ({
          value: operador.nombre_completo,
          label: operador.nombre_completo
        })));
      } catch (error) {
        console.error('Error cargando catálogos de Fuel America:', error);
        Swal.fire('Aviso', 'No fue posible cargar uno o más catálogos.', 'warning');
      } finally {
        setLoadingCatalogs(false);
      }
    };

    cargarCatalogos();
  }, []);

  const assetOptions = form.asset_type === 'TRACTOR'
    ? tractorCatalog
    : form.asset_type === 'CAJA'
      ? cajaCatalog
      : [];

  const selectedAsset = assetOptions.find((option) => option.value === form.asset)
    || (form.asset ? { value: form.asset, label: form.asset } : null);

  const selectedDriver = operadores.find((option) => option.value === form.driver)
    || (form.driver ? { value: form.driver, label: form.driver } : null);

  const change = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const guardar = async () => {
    const gallons = Number(form.gallons);
    const amount = Number(form.amount);

    if (!form.date || !form.asset_type || !form.asset.trim() || !form.driver.trim() ||
        !Number.isFinite(gallons) || gallons <= 0 ||
        !Number.isFinite(amount) || amount < 0) {
      return Swal.fire('Datos incompletos', 'Llena correctamente todos los campos.', 'warning');
    }

    setSaving(true);
    try {
      const payload = {
        date: form.date.format('YYYY-MM-DD HH:mm:ss'),
        asset_type: form.asset_type,
        asset: form.asset.trim(),
        driver: form.driver.trim(),
        gallons,
        amount
        // Si tienes el usuario disponible, agrega: usuario: nombreUsuario
      };

      const response = isEditing
        ? await myApi.put(`/cargasFuel/editarFuelAmerica/${carga.id}`, payload)
        : await myApi.post('/cargasFuel/crearFuelAmerica', payload);

      if (!response.data.success) throw new Error(response.data.error || 'No se pudo guardar');

      onSaved(response.data.data);
      onClose();
      Swal.fire('Listo', isEditing ? 'Carga actualizada.' : 'Carga creada.', 'success');
    } catch (error) {
      Swal.fire('Error', error.response?.data?.error || error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>{isEditing ? 'Editar carga Fuel America' : 'Crear carga Fuel America'}</DialogTitle>
      <DialogContent sx={{ pt: '16px !important' }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <DateTimePicker
                label="FECHA"
                value={form.date}
                onChange={(value) => setForm((current) => ({ ...current, date: value }))}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel>TIPO DE UNIDAD</InputLabel>
                <Select
                  value={form.asset_type}
                  label="TIPO DE UNIDAD"
                  onChange={(event) => setForm((current) => ({
                    ...current,
                    asset_type: event.target.value,
                    asset: ''
                  }))}
                >
                  <MenuItem value="TRACTOR">TRACTOR</MenuItem>
                  <MenuItem value="CAJA">CAJA</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Autocomplete
                options={assetOptions}
                value={selectedAsset}
                disabled={!form.asset_type || loadingCatalogs}
                loading={loadingCatalogs}
                getOptionLabel={(option) => option?.label || ''}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                onChange={(_, option) => setForm((current) => ({
                  ...current,
                  asset: option?.value || ''
                }))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="UNIDAD"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingCatalogs ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                options={operadores}
                value={selectedDriver}
                loading={loadingCatalogs}
                getOptionLabel={(option) => option?.label || ''}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                onChange={(_, option) => setForm((current) => ({
                  ...current,
                  driver: option?.value || ''
                }))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="OPERADOR"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingCatalogs ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth label="CANTIDAD GLLS" type="number" value={form.gallons}
                onChange={change('gallons')} inputProps={{ min: 0.001, step: 0.001 }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth label="IMPORTE" type="number" value={form.amount}
                onChange={change('amount')} inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>CANCELAR</Button>
        <Button variant="contained" onClick={guardar} disabled={saving}>
          {saving ? 'GUARDANDO...' : 'GUARDAR'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CrearEditarFuelAmerica;